import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    Chip,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    TextField,
    IconButton,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    ListItemButton,
    Divider,
    Avatar
} from '@mui/material';
import { 
    Sync as SyncIcon, 
    Refresh as RefreshIcon, 
    Delete as DeleteIcon,
    Search as SearchIcon,
    PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';

interface Doctor {
    id: string;
    code: string;
    first_name: string;
    last_name: string;
    name: string;
    fiscal_code: string;
    regional_code: string;
    type: string;
    created_at: string;
    updated_at: string;
}

const DoctorsPage: React.FC = () => {
    
    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [doctorTypes, setDoctorTypes] = useState<{[key: number]: string}>({});
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        doctor: null as Doctor | null,
        loading: false
    });

    // Sync dialog per input ricerca medico
    const [syncDialog, setSyncDialog] = useState({
        open: false,
        fiscalCode: '',
        firstName: '',
        lastName: '',
        loading: false
    });

    // Selection dialog per medici multipli
    const [selectionDialog, setSelectionDialog] = useState({
        open: false,
        doctors: [] as Doctor[],
        selectedDoctors: [] as Doctor[],
        loading: false,
        searchCriteria: ''
    });

    const fetchDoctors = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                limit: rowsPerPage.toString(),
                offset: (page * rowsPerPage).toString()
            });

            const response = await apiClient.get(`/recup-sync/doctors?${params}`);
            
            if (response.data.success) {
                setDoctors(response.data.data);
                setTotalCount(response.data.pagination?.total || response.data.data.length);
            } else {
                setError(response.data.error || 'Errore nel recupero dei medici');
                console.error('👨‍⚕️ DoctorsPage: API returned error:', response.data.error);
            }
        } catch (err: any) {
            console.error('👨‍⚕️ DoctorsPage: API call failed:', err);
            setError(err.response?.data?.error || err.message || 'Errore nel recupero dei medici');
        } finally {
            setLoading(false);
        }
    };

    const fetchDoctorTypes = async () => {
        try {
            const response = await apiClient.get('/recup-sync/doctor-types');
            if (response.data.success) {
                const typesMap: {[key: number]: string} = {};
                response.data.data.forEach((type: any) => {
                    typesMap[type.id] = type.name;
                });
                setDoctorTypes(typesMap);
            }
        } catch (err: any) {
            console.warn('👨‍⚕️ DoctorsPage: Error fetching doctor types:', err.message);
        }
    };

    const syncDoctorTypes = async () => {
        try {
            await apiClient.post('/recup-sync/doctor-types/sync');
            await fetchDoctorTypes();
        } catch (err: any) {
            console.warn('👨‍⚕️ DoctorsPage: Error syncing doctor types:', err.message);
        }
    };

    const handleSyncBySearch = async () => {
        setSyncDialog(prev => ({ ...prev, loading: true }));
        setError(null);
        
        // Validazione: richiede codice fiscale O almeno cognome
        const hasValidData = syncDialog.fiscalCode.trim() || syncDialog.lastName.trim();
        if (!hasValidData) {
            setError('Inserire almeno il codice fiscale o il cognome del medico');
            setSyncDialog(prev => ({ ...prev, loading: false }));
            return;
        }

        try {
            // Prepara parametri per la ricerca
            const searchParams: any = {};
            if (syncDialog.fiscalCode.trim()) {
                searchParams.fiscalCode = syncDialog.fiscalCode.trim();
            }
            if (syncDialog.firstName.trim()) {
                searchParams.firstName = syncDialog.firstName.trim();
            }
            if (syncDialog.lastName.trim()) {
                searchParams.lastName = syncDialog.lastName.trim();
            }

            const response = await apiClient.get(`/recup-sync/doctors/search`, {
                params: searchParams
            });
            
            if (response.data.success && response.data.found && response.data.multiple) {
                // Medici multipli trovati - apri modale di selezione
                const searchCriteria = [
                    searchParams.fiscalCode && `CF: ${searchParams.fiscalCode}`,
                    searchParams.firstName && `Nome: ${searchParams.firstName}`,
                    searchParams.lastName && `Cognome: ${searchParams.lastName}`
                ].filter(Boolean).join(', ');

                setSyncDialog({ open: false, fiscalCode: '', firstName: '', lastName: '', loading: false });
                setSelectionDialog({
                    open: true,
                    doctors: response.data.data,
                    selectedDoctors: [],
                    loading: false,
                    searchCriteria
                });
            } else if (response.data.success && response.data.found && !response.data.multiple) {
                // Singolo medico trovato e sincronizzato con successo
                setSyncDialog({ open: false, fiscalCode: '', firstName: '', lastName: '', loading: false });
                await fetchDoctors();
                alert(`Sincronizzazione completata con successo!\n${response.data.message}`);
            } else if (response.data.success === false && response.data.found === false) {
                // Medico non trovato - messaggio informativo, non errore
                setSyncDialog({ open: false, fiscalCode: '', firstName: '', lastName: '', loading: false });
                alert(`Medico non trovato:\n${response.data.message}`);
            } else {
                // Errore vero e proprio
                setError(response.data.error || response.data.message || 'Errore nella sincronizzazione');
                setSyncDialog(prev => ({ ...prev, loading: false }));
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nella sincronizzazione');
            setSyncDialog(prev => ({ ...prev, loading: false }));
        }
    };

    const handleDoctorSelection = (doctor: Doctor, isSelected: boolean) => {
        setSelectionDialog(prev => ({
            ...prev,
            selectedDoctors: isSelected 
                ? [...prev.selectedDoctors, doctor]
                : prev.selectedDoctors.filter(d => d.id !== doctor.id)
        }));
    };

    const handleSelectAll = () => {
        setSelectionDialog(prev => ({
            ...prev,
            selectedDoctors: prev.selectedDoctors.length === prev.doctors.length 
                ? [] 
                : [...prev.doctors]
        }));
    };

    const handleSyncSelectedDoctors = async () => {
        setSelectionDialog(prev => ({ ...prev, loading: true }));
        setError(null);

        try {
            const response = await apiClient.post('/recup-sync/doctors/sync-selected', {
                doctors: selectionDialog.selectedDoctors
            });

            if (response.data.success) {
                setSelectionDialog({ open: false, doctors: [], selectedDoctors: [], loading: false, searchCriteria: '' });
                await fetchDoctors();
                alert(`Sincronizzazione completata con successo!\n${response.data.message}`);
            } else {
                setError(response.data.error || 'Errore nella sincronizzazione');
                setSelectionDialog(prev => ({ ...prev, loading: false }));
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nella sincronizzazione');
            setSelectionDialog(prev => ({ ...prev, loading: false }));
        }
    };

    const handleDeleteDoctor = async () => {
        if (!deleteDialog.doctor) return;
        
        setDeleteDialog(prev => ({ ...prev, loading: true }));
        setError(null);
        try {
            const response = await apiClient.delete(`/doctors/${deleteDialog.doctor.id}`);
            if (response.data.success) {
                setDeleteDialog({ open: false, doctor: null, loading: false });
                await fetchDoctors();
                alert('Medico eliminato dal database locale con successo!');
            } else {
                setError(response.data.error || 'Errore nell\'eliminazione');
                setDeleteDialog(prev => ({ ...prev, loading: false }));
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nell\'eliminazione');
            setDeleteDialog(prev => ({ ...prev, loading: false }));
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getTypeChip = (type: string | number | undefined) => {
        const typeName = getDoctorTypeName(type);
        
        const colorMap: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' } = {
            'MEDICO': 'primary',
            'MEDICO DI BASE': 'primary',
            'MEDICO SPECIALISTA': 'success',
            'SPECIALISTA': 'success',
            'CONSULENTE': 'info',
            'PEDIATRA': 'warning',
            'ALTRO': 'default'
        };
        
        return <Chip 
            label={typeName} 
            color={colorMap[typeName.toUpperCase()] || 'default'} 
            size="small" 
        />;
    };

    useEffect(() => {
        
        const initializePage = async () => {
            // Prima sincronizza i tipi medico (se necessario), poi carica i medici
            await syncDoctorTypes();
            await fetchDoctors();
        };
        
        initializePage();
    }, [page, rowsPerPage]);

    const getDoctorTypeName = (doctorTypeId: number | string | undefined): string => {
        if (!doctorTypeId) return 'MEDICO';
        const typeId = typeof doctorTypeId === 'string' ? parseInt(doctorTypeId) : doctorTypeId;
        return doctorTypes[typeId] || `Tipo ${typeId}`;
    };

    
    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Gestione Medici
            </Typography>

            {/* Statistiche */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Totale Medici
                            </Typography>
                            <Typography variant="h5">
                                {totalCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Medici Specialisti
                            </Typography>
                            <Typography variant="h5">
                                {doctors.filter(d => d.type === 'SPECIALISTA').length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Medici Generici
                            </Typography>
                            <Typography variant="h5">
                                {doctors.filter(d => d.type === 'MEDICO').length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Consulenti
                            </Typography>
                            <Typography variant="h5">
                                {doctors.filter(d => d.type === 'CONSULENTE').length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Controlli */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        onClick={fetchDoctors}
                        disabled={loading}
                        startIcon={<RefreshIcon />}
                    >
                        Aggiorna
                    </Button>
                    
                    <Button
                        variant="contained"
                        onClick={() => setSyncDialog({ open: true, fiscalCode: '', firstName: '', lastName: '', loading: false })}
                        startIcon={<SearchIcon />}
                        color="primary"
                    >
                        Cerca Medico da RECUP
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Tabella Medici */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Codice</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Cognome</TableCell>
                                <TableCell>Nome Completo</TableCell>
                                <TableCell>Codice Fiscale</TableCell>
                                <TableCell>Codice Regionale</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Creato</TableCell>
                                <TableCell>Aggiornato</TableCell>
                                <TableCell>Azioni</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={11}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : doctors.length === 0 ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={11}>
                                        Nessun medico trovato
                                    </TableCell>
                                </TableRow>
                            ) : (
                                doctors.map((doctor) => (
                                    <TableRow key={doctor.id}>
                                        <TableCell>{doctor.id}</TableCell>
                                        <TableCell>{doctor.code}</TableCell>
                                        <TableCell>{doctor.first_name}</TableCell>
                                        <TableCell>{doctor.last_name}</TableCell>
                                        <TableCell>{doctor.name}</TableCell>
                                        <TableCell>{doctor.fiscal_code}</TableCell>
                                        <TableCell>{doctor.regional_code}</TableCell>
                                        <TableCell>{getTypeChip(doctor.type)}</TableCell>
                                        <TableCell>
                                            {new Date(doctor.created_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(doctor.updated_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Elimina medico dal database locale">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setDeleteDialog({ 
                                                        open: true, 
                                                        doctor, 
                                                        loading: false 
                                                    })}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Righe per pagina:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                    }
                />
            </Paper>

            {/* Dialog per eliminazione medico */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => !deleteDialog.loading && setDeleteDialog({ open: false, doctor: null, loading: false })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Conferma Eliminazione
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sei sicuro di voler eliminare il medico <strong>{deleteDialog.doctor?.first_name} {deleteDialog.doctor?.last_name}</strong> dal database locale?
                        <br /><br />
                        <strong>Attenzione:</strong> Questa operazione è irreversibile e eliminerà il medico solo dal database locale, non da RECUP.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialog({ open: false, doctor: null, loading: false })}
                        disabled={deleteDialog.loading}
                    >
                        Annulla
                    </Button>
                    <Button
                        onClick={handleDeleteDoctor}
                        color="error"
                        variant="contained"
                        disabled={deleteDialog.loading}
                        startIcon={deleteDialog.loading ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        {deleteDialog.loading ? 'Eliminazione...' : 'Elimina'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog per sincronizzazione medico tramite ricerca estesa */}
            <Dialog
                open={syncDialog.open}
                onClose={() => !syncDialog.loading && setSyncDialog({ open: false, fiscalCode: '', firstName: '', lastName: '', loading: false })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Cerca Medico da RECUP
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Cerca un medico su RECUP utilizzando il <strong>codice fiscale</strong> o <strong>cognome</strong> (obbligatorio) + nome (opzionale).
                        Se il medico viene trovato, sarà aggiunto o aggiornato nel database locale.
                    </DialogContentText>
                    
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Codice Fiscale"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={syncDialog.fiscalCode}
                        onChange={(e) => setSyncDialog(prev => ({ ...prev, fiscalCode: e.target.value.toUpperCase() }))}
                        disabled={syncDialog.loading}
                        placeholder="es. RSSMRA80A01H501Z"
                        inputProps={{ maxLength: 16 }}
                        sx={{ mb: 2 }}
                        helperText="Ricerca diretta per codice fiscale (metodo preferito)"
                    />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                        <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
                            OPPURE
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    </Box>
                    
                    <TextField
                        margin="dense"
                        label="Cognome *"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={syncDialog.lastName}
                        onChange={(e) => setSyncDialog(prev => ({ ...prev, lastName: e.target.value }))}
                        disabled={syncDialog.loading}
                        placeholder="es. Rossi"
                        sx={{ mb: 1 }}
                        helperText="Obbligatorio per ricerca per nome"
                    />
                    
                    <TextField
                        margin="dense"
                        label="Nome (opzionale)"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={syncDialog.firstName}
                        onChange={(e) => setSyncDialog(prev => ({ ...prev, firstName: e.target.value }))}
                        disabled={syncDialog.loading}
                        placeholder="es. Mario"
                        helperText="Opzionale - migliora la precisione della ricerca"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setSyncDialog({ open: false, fiscalCode: '', firstName: '', lastName: '', loading: false })}
                        disabled={syncDialog.loading}
                    >
                        Annulla
                    </Button>
                    <Button
                        onClick={handleSyncBySearch}
                        color="primary"
                        variant="contained"
                        disabled={syncDialog.loading || (!syncDialog.fiscalCode.trim() && !syncDialog.lastName.trim())}
                        startIcon={syncDialog.loading ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                        {syncDialog.loading ? 'Ricerca...' : 'Cerca e Sincronizza'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog per selezione medici multipli */}
            <Dialog
                open={selectionDialog.open}
                onClose={() => !selectionDialog.loading && setSelectionDialog({ open: false, doctors: [], selectedDoctors: [], loading: false, searchCriteria: '' })}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            Seleziona Medici da Sincronizzare
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {selectionDialog.searchCriteria}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Trovati <strong>{selectionDialog.doctors.length} medici</strong> corrispondenti ai criteri di ricerca. 
                        Seleziona uno o più medici da sincronizzare con il database locale.
                    </DialogContentText>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Checkbox
                            checked={selectionDialog.selectedDoctors.length === selectionDialog.doctors.length && selectionDialog.doctors.length > 0}
                            indeterminate={selectionDialog.selectedDoctors.length > 0 && selectionDialog.selectedDoctors.length < selectionDialog.doctors.length}
                            onChange={handleSelectAll}
                            disabled={selectionDialog.loading}
                        />
                        <Typography variant="body2">
                            Seleziona tutti ({selectionDialog.selectedDoctors.length} di {selectionDialog.doctors.length})
                        </Typography>
                    </Box>

                    <List sx={{ width: '100%', maxHeight: 400, overflow: 'auto' }}>
                        {selectionDialog.doctors.map((doctor, index) => {
                            const isSelected = selectionDialog.selectedDoctors.some(d => d.id === doctor.id);
                            return (
                                <React.Fragment key={doctor.id}>
                                    <ListItemButton
                                        onClick={() => handleDoctorSelection(doctor, !isSelected)}
                                        disabled={selectionDialog.loading}
                                    >
                                        <Checkbox
                                            checked={isSelected}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                            {doctor.first_name?.charAt(0)}{doctor.last_name?.charAt(0)}
                                        </Avatar>
                                        <ListItemText
                                            primary={`${doctor.first_name} ${doctor.last_name}`}
                                            secondary={
                                                <React.Fragment>
                                                    <Typography variant="body2" component="span" display="block">
                                                        <strong>CF:</strong> {doctor.fiscal_code}
                                                    </Typography>
                                                    <Typography variant="body2" component="span" display="block">
                                                        <strong>Tipo:</strong> {doctor.type} | <strong>Cod. Reg.:</strong> {doctor.regional_code}
                                                    </Typography>
                                                    {doctor.region_name && (
                                                        <Typography variant="body2" component="span" display="block">
                                                            <strong>Regione:</strong> {doctor.region_name}
                                                        </Typography>
                                                    )}
                                                </React.Fragment>
                                            }
                                        />
                                        <Chip 
                                            label={doctor.type || 'MEDICO'} 
                                            size="small" 
                                            color={doctor.active ? 'success' : 'default'}
                                        />
                                    </ListItemButton>
                                    {index < selectionDialog.doctors.length - 1 && <Divider />}
                                </React.Fragment>
                            );
                        })}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setSelectionDialog({ open: false, doctors: [], selectedDoctors: [], loading: false, searchCriteria: '' })}
                        disabled={selectionDialog.loading}
                    >
                        Annulla
                    </Button>
                    <Button
                        onClick={handleSyncSelectedDoctors}
                        color="primary"
                        variant="contained"
                        disabled={selectionDialog.loading || selectionDialog.selectedDoctors.length === 0}
                        startIcon={selectionDialog.loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                    >
                        {selectionDialog.loading ? 'Sincronizzazione...' : `Sincronizza ${selectionDialog.selectedDoctors.length} medici`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default DoctorsPage;